import appConfig from "@config/app";
import { UPGRADE_REQUIRED } from "@shared/libs/oh-errors/error-messages";
import { manageTooManyRequestError } from "@shared/libs/oh-errors/manage-too-many-request-error";
import { pauseOhQueries, rateLimitDelayFromHeaders } from "@shared/libs/react-query/query-client";
import { OhApi } from "@shared/libs/siayuda";
import { ApiResponse } from "@shared/libs/siayuda/oh-api/types";
import { getCurrentLegalEntityUuid } from "hassibot/legal-entity-session-cache";

import { getMicrosoftToken, msalInstance, ReauthRequiredError } from "./login/msal";

class LegalEntityRestrictedApiClient extends OhApi.ApiV2Client {
  // siayuda's updateToken clones the current AuthConfig, so it can never
  // transition from a logged-out client (auth: null): rebuild it instead.
  public updateToken = (newToken: string): void => {
    this.config.auth = new OhApi.AuthConfig("HB", newToken);
  };

  public buildHeaders = (additionalHeaders?: Record<string, string>): Headers => {
    const currentLegalEntityUuid = getCurrentLegalEntityUuid();
    return new Headers({
      ...(this.config.auth && this.config.auth.makeAuthHeader()),
      ...(currentLegalEntityUuid && { "X-LEGAL-ENTITY-UUID": currentLegalEntityUuid }),
      "X-HB-RELEASE": appConfig.BUILD_HASH,
      ...additionalHeaders,
    });
  };
}

const TOO_MANY_REQUESTS_HTTP_CODE = 429;
const UNAUTHORIZED_HTTP_CODE = 401;
export const BAD_REQUEST_HTTP_CODE = 400;

export const OPEN_UPGRADE_MODAL = "OPEN_UPGRADE_MODAL";

/**
 * API client with automated token acquisition and response handling.
 * - Starts logged out (auth: null); a token is acquired before every
 *   request by the `client` wrapper below
 * - MSAL handles caching internally, so minimal performance overhead
 * - Response hooks handle 401 (logout), 429 (rate limiting), and upgrade prompts
 */
export const baseClient = new LegalEntityRestrictedApiClient({
  auth: null,
  preProcessingResponseHooks: [
    async (resp): Promise<void> => {
      if (resp.status === UNAUTHORIZED_HTTP_CODE) {
        await msalInstance.logoutRedirect({
          onRedirectNavigate: () => false,
        });
      } else if (resp.status === TOO_MANY_REQUESTS_HTTP_CODE) {
        manageTooManyRequestError();
        pauseOhQueries(rateLimitDelayFromHeaders(resp.headers));
      }
      // in case of blacklisted build hash version, the proxy return 400 with error "UPGRADE_REQUIRED" in body
      if (resp.status === BAD_REQUEST_HTTP_CODE) {
        // Clone the response before reading it to avoid consuming the body stream
        const clonedResp = resp.clone();
        const responseJson = await clonedResp.json();
        if (responseJson.error === UPGRADE_REQUIRED) {
          window.postMessage({ type: OPEN_UPGRADE_MODAL }, "*");
        }
      }
    },
  ],
});

let pendingAcquisition: Promise<void> | null = null;

const holdsRealToken = (): boolean => baseClient.config.auth !== null;

/**
 * Acquire a fresh Microsoft token and store it on the client. Never throws:
 * on failure the caller decides what to do based on holdsRealToken().
 */
const acquireToken = async (): Promise<void> => {
  try {
    const authResult = await getMicrosoftToken();
    baseClient.updateToken(authResult.accessToken);
  } catch (error) {
    console.error("[API Client] Failed to acquire token for API request", error);
    // With a real (possibly stale) token, let the request proceed:
    // 401 handler will trigger logout if needed
    if (holdsRealToken()) return;
    if (error instanceof ReauthRequiredError) {
      // Never had a token and re-auth is impossible (popup blocked or
      // dismissed): without this logout the user would stay stuck on an
      // authenticated UI where every request short-circuits.
      try {
        await msalInstance.logoutRedirect({ onRedirectNavigate: () => false });
      } catch (logoutError) {
        console.error("[API Client] Failed to logout after re-auth failure", logoutError);
      }
    }
  }
};

/**
 * Acquire fresh Microsoft token before request.
 * Pattern from dsit: get token on every request to guarantee freshness.
 * MSAL handles caching internally, so minimal performance overhead.
 *
 * Single-flight: concurrent requests share one in-flight acquisition
 * instead of each calling MSAL (which throws interaction_in_progress
 * when a popup is already open, e.g. N parallel requests at boot).
 *
 * Returns whether the client holds a real (possibly stale) token. Without
 * one the request must NOT go out: all token-less clients look identical,
 * so the API rate limit (keyed on the Authorization header) groups them
 * all in one shared bucket, and its 429 responses prevent the 401-logout
 * path from ever running.
 */
const acquireTokenForRequest = async (): Promise<boolean> => {
  await (pendingAcquisition ??= acquireToken().finally(() => {
    pendingAcquisition = null;
  }));
  return holdsRealToken();
};

const missingTokenFailure = (url: string, method: string): ApiResponse<never> => ({
  kind: "FETCH_ERROR",
  error: new Error("MISSING_AUTH_TOKEN"),
  request: new Request(url, { method }),
});

/**
 * Guard a baseClient method behind token acquisition: without a real token
 * the request never goes out. The wrapped method stays generic in the
 * response type because no parameter of the siayuda methods depends on it.
 */
const withTokenGuard =
  <Args extends [url: string, ...rest: unknown[]]>(
    httpVerb: string,
    call: (...args: Args) => Promise<ApiResponse<unknown>>
  ) =>
  async <T>(...args: Args): Promise<ApiResponse<T>> => {
    if (!(await acquireTokenForRequest())) return missingTokenFailure(args[0], httpVerb);
    return (await call(...args)) as ApiResponse<T>;
  };

/**
 * Wrapped API client with per-request token acquisition.
 * - Gets token on EVERY request to ensure it's always fresh
 * - MSAL handles caching internally, so minimal performance overhead
 */
export const client = {
  ...baseClient,
  get: withTokenGuard("GET", baseClient.get),
  post: withTokenGuard("POST", baseClient.post),
  put: withTokenGuard("PUT", baseClient.put),
  httpDelete: withTokenGuard("DELETE", baseClient.httpDelete),
  postJson: withTokenGuard("POST", baseClient.postJson),
  putJson: withTokenGuard("PUT", baseClient.putJson),
  deleteJson: withTokenGuard("DELETE", baseClient.deleteJson),
};
